import {Localization} from '@mui/material/locale';
import {PickersLocaleText} from '@mui/x-date-pickers';
import {addMinutes, subMinutes, parse, setHours, setMilliseconds, setMinutes, setSeconds, isValid, Locale, format} from 'date-fns';
import {some} from 'lodash';
import {IntlShape} from 'react-intl';

import {ApplicationLocale} from 'src/js/common/types';
import {FORMATTED_PROPERTY_TYPE_DATE, FORMATTED_PROPERTY_TYPE_DATETIME, FormattedProperty} from 'src/js/model/formatted_property';
import {I18nConfig, I18nConfigRaw} from 'src/js/model/i18n_config';

export type AllowedDateFnsLocale = 'cs' | 'de' | 'en-US' | 'sk' | 'pl';
export type AllowedMuiLocale = 'csCZ' | 'deDE' | 'enUS' | 'skSK' | 'plPL';

type oneToNine = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;
type zeroToNine = 0 | oneToNine;

/**
 * Years
 */
type YYYY = `19${zeroToNine}${zeroToNine}` | `20${zeroToNine}${zeroToNine}`;
/**
 * Months
 */
type MM = `0${oneToNine}` | `1${0 | 1 | 2}`;
/**
 * Days
 */
type DD = `${0}${oneToNine}` | `${1 | 2}${zeroToNine}` | `3${0 | 1}`;

/**
 * YYYYMMDD
 */
export type RawDateString = `${YYYY}${MM}${DD}`;
export type RawISODateOnlyString = `${YYYY}-${MM}-${DD}`;

export type LastWeekMonthIso = {
  lastWeekStart: string;
  lastWeekEnd: string;
  olderThan7DaysStart: string;
  olderThan7DaysEnd: string;
  olderThan30DaysStart: string;
  olderThan30DaysEnd: string;
};

export function isValidISODateOnlyString(str: string): str is RawISODateOnlyString {
  return str.match(/^\d{4}-\d{2}-\d{2}$/) !== null;
}

export const DateUtils = {
  ISO_DATE_ONLY_FORMAT: 'yyyy-MM-dd',
  ISO_DATE_TIME_FORMAT: "yyyy-MM-dd'T'HH:mm:ss",
  DEFAULT_DATE_FORMAT: 'd. M. yyyy',
  MONTH_FORMAT: 'MM/yyyy',
  REVERSE_MONTH_FORMAT: 'yyyy/MM',

  resetTime(date: number | string | Date) {
    let dateObj = new Date(date);
    dateObj = setHours(dateObj, 0);
    dateObj = setMinutes(dateObj, 0);
    dateObj = setSeconds(dateObj, 0);
    dateObj = setMilliseconds(dateObj, 0);

    return dateObj;
  },

  translateLocaleToDateFnsLocale(locale: ApplicationLocale): AllowedDateFnsLocale {
    switch (locale) {
      case 'cs':
        return 'cs';
      case 'de':
        return 'de';
      case 'en':
        return 'en-US';
      case 'sk':
        return 'sk';
      case 'pl':
        return 'pl';
      default:
        return locale;
    }
  },

  translateLocaleToMuiLocale(locale: ApplicationLocale): AllowedMuiLocale {
    switch (locale) {
      case 'cs':
        return 'csCZ';
      case 'de':
        return 'deDE';
      case 'en':
        return 'enUS';
      case 'sk':
        return 'skSK';
      case 'pl':
        return 'plPL';
      default:
        return locale;
    }
  },

  getLocaleDateFnsFormatPattern(locale: ApplicationLocale): string[] {
    switch (locale) {
      case 'cs':
      case 'de':
      case 'sk':
      case 'pl':
        return ['d. M. y', 'd.M.y', 'dd.MM.yyyy', 'dd. MM. yyyy', 'd. M.y', 'dd. MM.yyyy', 'd.M. y', 'dd.MM. yyyy'];
      default:
        return ['M/d/y', 'P'];
    }
  },

  getLocaleD3FormatPattern(locale: ApplicationLocale): string {
    switch (locale) {
      case 'cs':
        return '%d.%m.%Y';
      case 'de':
        return '%d.%m.%Y';
      case 'pl':
        return '%d.%m.%Y';
      case 'en':
        return '%m/%d/%Y';
      case 'sk':
        return '%d.%m.%Y';
      default:
        return '%m/%d/%Y';
    }
  },

  getLocaleD3TimeFormatPattern(locale: ApplicationLocale): string {
    switch (locale) {
      case 'cs':
        return '%-d.%-m.%y %H:%M';
      case 'de':
        return '%d.%m.%y %H:%M';
      case 'pl':
        return '%-d.%-m.%y %H:%M';
      case 'en':
        return '%-m/%-d/%y %-I:%M %p';
      case 'sk':
        return '%-d.%-m.%y %H:%M';
      default:
        return '%-m/%-d/%y %-I:%M %p';
    }
  },

  getLocaleD3TimeTickFormatStops(locale: ApplicationLocale): {dtickrange: [number | string | null, number | string | null]; value: string}[] {
    const dayMonth = locale === 'en' ? '%m/%d' : locale === 'de' || locale === 'pl' ? '%d.%m.' : '%d. %m.';
    const time = locale === 'en' ? '%I:%M %p' : '%H:%M';
    const timeWithSeconds = locale === 'en' ? '%I:%M:%S %p' : '%H:%M:%S';

    return [
      {dtickrange: [null, 1000], value: `${timeWithSeconds} \n ${dayMonth}`},
      {dtickrange: [1000, 60000], value: `${timeWithSeconds} \n ${dayMonth}`},
      {dtickrange: [60000, 3600000], value: `${time} \n ${dayMonth}`},
      {dtickrange: [3600000, 82800000], value: `${time} \n ${dayMonth}`},
      {dtickrange: [82800000, 86400000], value: `${dayMonth} \n %y`},
      {dtickrange: [86400000, 604800000], value: `${dayMonth} \n %y`},
      {dtickrange: [604800000, 'M1'], value: `${dayMonth} \n %y`},
      {dtickrange: ['M1', 'M12'], value: "%m '%y"},
      {dtickrange: ['M12', null], value: '%Y'},
    ];
  },

  getLocaleMonthYearPattern(): string {
    return '%m/%y';
  },

  isDate(date: string, formats: string[] = ['MM-dd-yyyy', 'yyyy-MM-dd', 'yyyy/MM/dd', 'dd.MM.yyyy', 'd.M.yyyy']): boolean {
    return some(formats, (format) => isValid(parse(date, format, new Date())));
  },

  /**
   * Convert given date to UTC
   * @link https://github.com/date-fns/date-fns/issues/556#issuecomment-391048347
   * @param date | plain JS date object
   * @return {Date | *}
   */
  toUtc(date: Date): Date {
    const offset = date.getTimezoneOffset();

    return Math.sign(offset) === -1 ? subMinutes(date, Math.abs(offset)) : addMinutes(date, offset);
  },

  /* eslint-disable import/no-dynamic-require, global-require */
  loadLocaleData(i18nConfig?: I18nConfigRaw): Record<string, Locale> {
    const i18nConfigModel = new I18nConfig(i18nConfig);
    const loadedLocales: Record<string, Locale> = {};
    i18nConfigModel.getUsedApplicationLanguages()?.forEach((language) => {
      const dateLocale = DateUtils.translateLocaleToDateFnsLocale(language.getLocale());
      // load locale data for all supported languages
      // eslint-disable-next-line @typescript-eslint/no-var-requires

      loadedLocales[dateLocale] = require(`date-fns/locale/${dateLocale}/index.js`);
    });
    return loadedLocales;
  },

  loadMuiXDatePickersLocaleText(i18nConfig?: I18nConfigRaw) {
    const i18nConfigModel = new I18nConfig(i18nConfig);
    const loadedLocaleTexts: Record<string, PickersLocaleText<any>> = {};

    i18nConfigModel.getUsedApplicationLanguages()?.forEach((language) => {
      const muiLocale = DateUtils.translateLocaleToMuiLocale(language.getLocale());
      // load locale text from @mui/x-date-pickers for all supported languages
      // eslint-disable-next-line @typescript-eslint/no-var-requires

      loadedLocaleTexts[muiLocale] = require(`@mui/x-date-pickers/locales/${muiLocale}`);
    });

    return loadedLocaleTexts;
  },

  loadMuiLocaleText(i18nConfig?: I18nConfigRaw) {
    const i18nConfigModel = new I18nConfig(i18nConfig);
    const loadedLocaleTexts: Record<string, Localization> = {};

    i18nConfigModel.getUsedApplicationLanguages()?.forEach((language) => {
      const muiLocale = DateUtils.translateLocaleToMuiLocale(language.getLocale());
      // load locale text from @mui/material for all supported languages
      // eslint-disable-next-line @typescript-eslint/no-var-requires

      const allLocalizedTexts = require(`@mui/material/locale`);

      loadedLocaleTexts[muiLocale] = allLocalizedTexts[muiLocale];
    });

    return loadedLocaleTexts;
  },

  getFormattedDate(
    value: Date | string | null | undefined,
    translateService: IntlShape,
    dateOnly: boolean = false,
    shortFormat: boolean = false,
    dayMonthOnly: boolean = false
  ): string | undefined | null {
    if (value == null || Number.isNaN(+new Date(value))) return 'Invalid Date';

    let formattedValue: string;

    formattedValue =
      translateService && value
        ? new Intl.DateTimeFormat(translateService.locale, {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: dateOnly ? undefined : '2-digit',
            minute: dateOnly ? undefined : 'numeric',
          })
            .format(new Date(value))
            .replace(',', '')
        : '';

    if (shortFormat) {
      formattedValue =
        translateService && value
          ? new Intl.DateTimeFormat(translateService.locale, {
              year: '2-digit',
              month: '2-digit',
              day: '2-digit',
              hour: dateOnly ? undefined : 'numeric',
              minute: dateOnly ? undefined : 'numeric',
            })
              .format(new Date(value))
              .replace(',', '')
          : '';
    }

    if (dayMonthOnly) {
      formattedValue =
        translateService && value
          ? new Intl.DateTimeFormat(translateService.locale, {
              month: '2-digit',
              day: '2-digit',
              hour: dateOnly ? undefined : 'numeric',
              minute: dateOnly ? undefined : 'numeric',
            })
              .format(new Date(value))
              .replace(',', '')
          : '';
    }
    return formattedValue;
  },
  toIsoDateTimeString(date: Date): string {
    return date.toISOString();
  },
  toIsoDateString(date: Date): string {
    return date.toISOString().split('T')[0];
  },
  toIsoDateStringLocal(localDate: Date): string {
    const year = localDate.getFullYear();
    const month = String(localDate.getMonth() + 1).padStart(2, '0');
    const day = String(localDate.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  },
  dateTimeStringToDateString(dateTimeString: string) {
    return dateTimeString.split('T')[0];
  },

  today(): Date {
    const acc = new Date();
    acc.setHours(0, 0, 0, 0);
    return acc;
  },
  startOfWeek(date: Date): Date {
    const acc = new Date(date);
    const offset = (acc.getDay() + 6) % 7;
    acc.setDate(date.getDate() - offset);
    acc.setHours(0, 0, 0, 0);
    return acc;
  },
  endOfWeek(date: Date): Date {
    const acc = new Date(date);
    const offset = ((7 - acc.getDay()) % 7) + 1;
    acc.setDate(date.getDate() + offset);
    acc.setHours(0, 0, 0, 0);
    return acc;
  },
  endOfDayIsoString(isoDateString: string): string {
    const acc = isoDateString ? new Date(isoDateString) : new Date();
    acc.setUTCHours(23, 59, 59, 999);
    return DateUtils.toIsoDateTimeString(acc);
  },
  nextMidnightIsoString(isoDateString: string): string {
    const acc = isoDateString ? new Date(isoDateString) : new Date();
    acc.setUTCDate(acc.getUTCDate() + 1);
    acc.setUTCHours(0, 0, 0, 0);
    return DateUtils.toIsoDateTimeString(acc);
  },
  /** get monthString from localDate */
  toMonthFormat(date: Date): string {
    return format(date, this.MONTH_FORMAT);
  },
  /** get localDate from monthString */
  fromMonthFormat(monthString: string): Date {
    return parse(monthString, this.MONTH_FORMAT, new Date());
  },
  /** get reverseMonthString from localDate */
  toReverseMonthFormat(date: Date): string {
    if (Number.isNaN(+date)) return ''; // NOTE: hide error in chart if value is never used
    return format(date, this.REVERSE_MONTH_FORMAT);
  },
  /** get localDate from reverseMonthString */
  fromReverseMonthFormat(reverseMonthString: string): Date {
    return parse(reverseMonthString, this.REVERSE_MONTH_FORMAT, new Date());
  },
  /** get localDate from ISOFormatString */
  fromISOFormat(ISOFormatString: string): Date {
    return parse(ISOFormatString, this.ISO_DATE_ONLY_FORMAT, new Date());
  },
  startOfMonth(date: Date): Date {
    const acc = new Date(date);
    acc.setHours(0, 0, 0, 0);
    acc.setDate(1);
    return acc;
  },
  endOfMonth(date: Date): Date {
    const acc = this.startOfMonth(date);
    acc.setMonth(acc.getMonth() + 1);
    acc.setDate(acc.getDate() - 1);
    return acc;
  },
  startOfMonthUTC(date: Date): Date {
    const acc = new Date(date);
    acc.setUTCHours(0, 0, 0, 0);
    acc.setUTCDate(1);
    return acc;
  },
  endOfMonthUTC(date: Date): Date {
    const acc = this.startOfMonthUTC(date);
    acc.setUTCMonth(acc.getUTCMonth() + 1);
    acc.setUTCDate(acc.getUTCDate() - 1);
    return acc;
  },
  lastCompletedMonth(): [string, string] {
    const startOfCurrentMonth = this.startOfMonthUTC(new Date());
    const startOfLastMonth = new Date(startOfCurrentMonth);
    startOfLastMonth.setUTCMonth(startOfLastMonth.getUTCMonth() - 1);
    const endOfLastMonth = new Date(startOfCurrentMonth);
    endOfLastMonth.setUTCDate(endOfLastMonth.getUTCDate() - 1);
    endOfLastMonth.setUTCHours(23, 59, 59, 59);
    return [this.toIsoDateString(startOfLastMonth), this.toIsoDateString(endOfLastMonth)];
  },
  lastWeekMonthIso(): LastWeekMonthIso {
    const todayIsoString = this.toIsoDateStringLocal(this.today());
    const todayIso = new Date(`${todayIsoString}Z`);
    // olderThan30Days
    const epochIso = new Date('1970-1-1Z');
    const olderThan30DaysEnd = new Date(todayIso);
    olderThan30DaysEnd.setUTCDate(olderThan30DaysEnd.getUTCDate() - 31);
    // olderThan7Days
    const olderThan7DaysStart = new Date(todayIso);
    olderThan7DaysStart.setUTCDate(olderThan7DaysStart.getUTCDate() - 30);
    const olderThan7DaysEnd = new Date(todayIso);
    olderThan7DaysEnd.setUTCDate(olderThan7DaysEnd.getUTCDate() - 8);
    // last7Days
    const lastWeekStart = new Date(todayIso);
    lastWeekStart.setUTCDate(lastWeekStart.getUTCDate() - 7);
    return {
      lastWeekStart: this.toIsoDateString(lastWeekStart),
      lastWeekEnd: this.toIsoDateString(todayIso),
      olderThan7DaysStart: this.toIsoDateString(olderThan7DaysStart),
      olderThan7DaysEnd: this.toIsoDateString(olderThan7DaysEnd),
      olderThan30DaysStart: this.toIsoDateString(epochIso),
      olderThan30DaysEnd: this.toIsoDateString(olderThan30DaysEnd),
    };
  },
};

export default DateUtils;
